import React, { useState, useEffect } from 'react';
import { DiscoverFilters as DiscoverFiltersType } from '../../types/discover';
import {
  cleanlinessOptions as formCleanlinessOptions
} from '../../constants/formData';
import { boroughs, neighborhoods } from '../../data/locationData';

interface DiscoverFiltersProps {
  onApplyFilters: (filters: DiscoverFiltersType) => void;
  currentFilters: DiscoverFiltersType;
  onClearFilters: () => void;
  requireNeighborhoodOverlap?: boolean;
  onNeighborhoodOverlapChange?: (checked: boolean) => void;
  /** When true, only the Location section (borough pills + neighbourhood drill-down) is rendered. */
  locationOnly?: boolean;
}

const DiscoverFiltersComponent: React.FC<DiscoverFiltersProps> = ({
  onApplyFilters,
  currentFilters,
  onClearFilters,
  requireNeighborhoodOverlap = false,
  onNeighborhoodOverlapChange,
  locationOnly = false
}) => {
  const [filters, setFilters] = useState<DiscoverFiltersType>(currentFilters);
  const [expandedBoroughs, setExpandedBoroughs] = useState<Set<string>>(new Set());

  // Sync local state when parent clears (e.g. Clear All from header)
  useEffect(() => {
    setFilters(currentFilters);
    if (!currentFilters.locations?.length) setExpandedBoroughs(new Set());
  }, [currentFilters]);

  const handleFilterChange = (key: keyof DiscoverFiltersType, value: any) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    // Local state only; no API call until Apply Filter
  };

  const handleApplyFilters = () => {
    const filtersWithOverlap: DiscoverFiltersType = {
      ...filters,
      requireNeighborhoodOverlap
    };
    onApplyFilters(filtersWithOverlap);
  };

  const handleClearFilters = () => {
    setFilters({});
    onClearFilters();
  };

  const budgetOptions = [
    '$1000-1500', '$1500-2000', '$2000-2500', '$2500-3000', '$3000+'
  ];

const getActiveFilterCount = () => {
    if (locationOnly) {
      return filters.locations?.length ? 1 : 0;
    }
    let count = 0;
    if (filters.locations?.length) count++;
    if (filters.gender) count++;
    if (filters.budget) count++;
    if (filters.cleanliness) count++;
    if (filters.moveInDate) count++;
    if (filters.hasPets !== undefined) count++;
    return count;
  };

  return (
    <div className="bg-cream rounded-xl p-6 border-2 border-coffee shadow-[4px_4px_0px_#594A42]">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <h3 className="text-coffee font-semibold text-lg">Filters</h3>
          {getActiveFilterCount() > 0 && (
            <span className="bg-terracotta text-white border border-coffee text-xs px-2 py-1 rounded-full">
              {getActiveFilterCount()}
            </span>
          )}
        </div>
        <div className="flex space-x-2">
          <button
            onClick={handleApplyFilters}
            className="bg-terracotta text-white border-2 border-coffee px-4 py-2 rounded-lg shadow-[2px_2px_0px_#594A42] hover:bg-[#d89050] transition-colors text-sm font-medium"
          >
            Apply Filter
          </button>
          <button
            onClick={handleClearFilters}
            className="text-terracotta hover:underline text-sm transition-colors"
          >
            Clear All
          </button>
        </div>
      </div>

      <div className="space-y-6">
        {/* Location — selected boroughs as cards, unselected as a pill row */}
        <div>
          <label className="text-coffee/80 font-medium text-sm mb-2 block">Location</label>

          {/* Selected boroughs — each as its own card with neighborhood drill-down */}
          {boroughs.filter(b => filters.locations?.includes(b.value)).length > 0 && (
            <div className="space-y-2 mb-3">
              {boroughs.filter(b => filters.locations?.includes(b.value)).map(borough => {
                const isExpanded = expandedBoroughs.has(borough.value);
                const boroughNeighborhoods = (neighborhoods as Record<string, { value: string; label: string }[]>)[borough.value] || [];
                const selectedIds = new Set((filters.neighborhoodIds || []).filter(id => id.startsWith(borough.value + '-')));

                return (
                  <div key={borough.value} className="border border-coffee/20 rounded-lg p-2.5 bg-paper">
                    {/* Borough header row */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          const currentLocations = filters.locations || [];
                          const newLocations = currentLocations.filter(l => l !== borough.value);
                          const newNeighborhoodIds = (filters.neighborhoodIds || []).filter(id => !id.startsWith(borough.value + '-'));
                          setExpandedBoroughs(prev => { const next = new Set(prev); next.delete(borough.value); return next; });
                          setFilters({
                            ...filters,
                            locations: newLocations.length > 0 ? newLocations : undefined,
                            neighborhoodIds: newNeighborhoodIds.length > 0 ? newNeighborhoodIds : undefined
                          });
                        }}
                        className={`px-3 py-1 rounded-full text-xs border shadow-sm transition-colors ${
                          selectedIds.size > 0
                            ? 'bg-steel text-white border-coffee'
                            : 'bg-terracotta text-white border-coffee'
                        }`}
                      >
                        {borough.label}
                      </button>
                      <button
                        onClick={() => setExpandedBoroughs(prev => {
                          const next = new Set(prev);
                          isExpanded ? next.delete(borough.value) : next.add(borough.value);
                          return next;
                        })}
                        className="text-xs text-coffee/50 hover:text-coffee transition-colors"
                        aria-label={isExpanded ? 'Hide neighborhoods' : 'Show neighborhoods'}
                      >
                        {isExpanded ? 'hide ▲' : 'neighborhoods ▼'}
                      </button>
                      {!isExpanded && selectedIds.size > 0 && (
                        <span className="text-[10px] text-terracotta font-medium ml-auto">
                          {selectedIds.size} selected
                        </span>
                      )}
                    </div>

                    {/* Neighborhood pills */}
                    {isExpanded && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {boroughNeighborhoods.map(nbhd => {
                          const canonicalId = `${borough.value}-${nbhd.value}`;
                          const isNbhdSelected = selectedIds.has(canonicalId);
                          return (
                            <button
                              key={nbhd.value}
                              onClick={() => {
                                const currentNeighborhoodIds = filters.neighborhoodIds || [];
                                const newNeighborhoodIds = isNbhdSelected
                                  ? currentNeighborhoodIds.filter(id => id !== canonicalId)
                                  : [...currentNeighborhoodIds, canonicalId];
                                setFilters({
                                  ...filters,
                                  neighborhoodIds: newNeighborhoodIds.length > 0 ? newNeighborhoodIds : undefined
                                });
                              }}
                              className={`px-2.5 py-1 rounded-full text-xs transition-colors border ${
                                isNbhdSelected
                                  ? 'bg-terracotta text-white border-coffee shadow-sm'
                                  : 'bg-transparent border-coffee/30 text-coffee hover:bg-coffee/5'
                              }`}
                            >
                              {nbhd.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Unselected boroughs — compact pill row */}
          <div className="flex flex-wrap gap-2">
            {boroughs.filter(b => !filters.locations?.includes(b.value)).map(borough => (
              <button
                key={borough.value}
                onClick={() => {
                  const newLocations = [...(filters.locations || []), borough.value];
                  setExpandedBoroughs(prev => new Set(Array.from(prev).concat(borough.value)));
                  setFilters({ ...filters, locations: newLocations });
                }}
                className="px-3 py-1 rounded-full text-xs transition-colors border bg-transparent border-coffee/30 text-coffee hover:bg-coffee/5"
              >
                {borough.value === 'jersey-city' ? 'Jersey City' : borough.label}
              </button>
            ))}
          </div>
        </div>

        {/* Row 1: Move in by (1/4) + gap (1/4) + Neighborhood Overlap (1/2) — hidden in locationOnly mode */}
        {!locationOnly && (
          <div className="grid grid-cols-4 gap-3">
            <div className="col-span-1">
              <label className="text-coffee/80 font-medium text-sm mb-2 block">
                Move in by <span className="font-normal text-coffee/50">±1 month</span>
              </label>
              {(() => {
                const now = new Date();
                const currentYear = now.getFullYear();
                const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
                const existingParts = (filters.moveInDate || '').split('-');
                const selectedYear = existingParts[0] && existingParts[0].length === 4 ? existingParts[0] : '';
                const selectedMonth = existingParts[1] || '';
                const startYear = currentYear - 1;
                const yearOptions = Array.from({ length: 5 }, (_, i) => startYear + i);
                const getMonthOptions = () => monthNames
                  .map((name, idx) => ({ label: name, value: String(idx + 1).padStart(2, '0') }));
                const handleYearChange = (year: string) => {
                  if (!year) { handleFilterChange('moveInDate', undefined); return; }
                  const m = selectedMonth || '01';
                  handleFilterChange('moveInDate', `${year}-${m}-01`);
                };
                const handleMonthChange = (month: string) => {
                  if (!month) { handleFilterChange('moveInDate', undefined); return; }
                  if (!selectedYear) return;
                  handleFilterChange('moveInDate', `${selectedYear}-${month}-01`);
                };
                return (
                  <div className="flex gap-2">
                    <select
                      value={selectedMonth}
                      onChange={(e) => handleMonthChange(e.target.value)}
                      className="flex-1 bg-white text-coffee border border-coffee/30 rounded-lg px-2 py-2 focus:outline-none focus:border-terracotta focus:ring-1 focus:ring-terracotta text-sm"
                    >
                      <option value="" className="text-coffee bg-white">Any</option>
                      {getMonthOptions().map(m => (
                        <option key={m.value} value={m.value} className="text-coffee bg-white">{m.label}</option>
                      ))}
                    </select>
                    <select
                      value={selectedYear}
                      onChange={(e) => handleYearChange(e.target.value)}
                      className="flex-1 min-w-0 bg-white text-coffee border border-coffee/30 rounded-lg px-2 py-2 focus:outline-none focus:border-terracotta focus:ring-1 focus:ring-terracotta text-sm"
                    >
                      <option value="" className="text-coffee bg-white">Year</option>
                      {yearOptions.map(y => (
                        <option key={y} value={String(y)} className="text-coffee bg-white">{y}</option>
                      ))}
                    </select>
                  </div>
                );
              })()}
            </div>
            {/* empty spacer */}
            <div className="col-span-1" />
            {onNeighborhoodOverlapChange && (
              <div className="col-span-2">
                <label className="text-coffee/80 font-medium text-sm mb-2 block invisible">_</label>
                <div className="flex items-center gap-2 py-2">
                  <input
                    type="checkbox"
                    id="neighborhoodOverlap"
                    checked={requireNeighborhoodOverlap}
                    onChange={(e) => onNeighborhoodOverlapChange(e.target.checked)}
                    className="w-4 h-4 rounded focus:ring-2 focus:ring-terracotta disabled:opacity-50 border-coffee/30"
                  />
                  <label htmlFor="neighborhoodOverlap" className="text-coffee/80 text-sm cursor-pointer">
                    Show shared neighborhoods only
                  </label>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Row 2: Gender, Budget, Cleanliness, Pets — hidden in locationOnly mode */}
        {!locationOnly && (
          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="text-coffee/80 font-medium text-sm mb-2 block">Gender</label>
              <select
                aria-label="Gender"
                value={filters.gender || ''}
                onChange={(e) => handleFilterChange('gender', e.target.value || undefined)}
                className="w-full bg-white text-coffee border border-coffee/30 rounded-lg px-2 py-2 focus:outline-none focus:border-terracotta focus:ring-1 focus:ring-terracotta text-sm"
              >
                <option value="" className="text-coffee bg-white">Any</option>
                <option value="male" className="text-coffee bg-white">Male</option>
                <option value="female" className="text-coffee bg-white">Female</option>
              </select>
            </div>
            <div>
              <label className="text-coffee/80 font-medium text-sm mb-2 block">Budget</label>
              <select
                aria-label="Budget"
                value={filters.budget || ''}
                onChange={(e) => handleFilterChange('budget', e.target.value || undefined)}
                className="w-full bg-white text-coffee border border-coffee/30 rounded-lg px-2 py-2 focus:outline-none focus:border-terracotta focus:ring-1 focus:ring-terracotta text-sm"
              >
                <option value="" className="text-coffee bg-white">Any</option>
                {budgetOptions.map(budget => (
                  <option key={budget} value={budget} className="text-coffee bg-white">{budget}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-coffee/80 font-medium text-sm mb-2 block">Cleanliness</label>
              <select
                value={filters.cleanliness || ''}
                onChange={(e) => handleFilterChange('cleanliness', e.target.value || undefined)}
                className="w-full bg-white text-coffee border border-coffee/30 rounded-lg px-2 py-2 focus:outline-none focus:border-terracotta focus:ring-1 focus:ring-terracotta text-sm"
              >
                <option value="" className="text-coffee bg-white">Any</option>
                {formCleanlinessOptions.map((opt) => (
                  <option key={opt.value} value={opt.value} className="text-coffee bg-white">{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-coffee/80 font-medium text-sm mb-2 block">Pets</label>
              <select
                aria-label="Pets"
                value={filters.hasPets === undefined ? '' : filters.hasPets ? 'true' : 'false'}
                onChange={(e) => {
                  const v = e.target.value;
                  handleFilterChange('hasPets', v === '' ? undefined : v === 'true');
                }}
                className="w-full bg-white text-coffee border border-coffee/30 rounded-lg px-2 py-2 focus:outline-none focus:border-terracotta focus:ring-1 focus:ring-terracotta text-sm"
              >
                <option value="" className="text-coffee bg-white">Any</option>
                <option value="true" className="text-coffee bg-white">Has Pets</option>
                <option value="false" className="text-coffee bg-white">No Pets</option>
              </select>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default DiscoverFiltersComponent;
